import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  TrendingDown, Loader2, Lightbulb, DollarSign, Zap,
  ArrowDown, Trophy, Target, RefreshCw, CreditCard,
  Home, ChevronRight, AlertTriangle, CheckCircle, Flame, Snowflake
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell, Legend
} from "recharts";

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });

const typeIcons = { loan: DollarSign, credit_card: CreditCard, mortgage: Home };

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/95 border border-white/10 rounded-sm p-3 text-xs">
      <p className="text-zinc-400 mb-1.5">Month {label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-400">{p.name || p.dataKey}:</span>
          <span className="text-white font-mono">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function DebtPayoff() {
  const { api } = useAuth();
  const [overview, setOverview] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [whatIf, setWhatIf] = useState(null);
  const [aiPlan, setAiPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [extraPayment, setExtraPayment] = useState("200");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/debt-payoff");
      setOverview(res.data);
      if (res.data.debts.length > 0) {
        await runComparison(parseFloat(extraPayment) || 0);
      }
    } catch {
      toast.error("Failed to load debt data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const runComparison = async (extra) => {
    setComparing(true);
    try {
      const [compRes, wifRes] = await Promise.all([
        api.post("/debt-payoff/compare", { extra_payment: extra }),
        api.post("/debt-payoff/what-if", { extra_amounts: [0, 100, 250, 500, 1000], strategy: "avalanche" }),
      ]);
      setComparison(compRes.data);
      setWhatIf(wifRes.data);
    } catch {
      toast.error("Failed to compare strategies");
    } finally {
      setComparing(false);
    }
  };

  const runAiPlan = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/debt-payoff/ai-plan", { extra_payment: parseFloat(extraPayment) || 0 });
      setAiPlan(res.data);
      toast.success("AI plan generated");
    } catch {
      toast.error("Failed to generate AI plan");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Debt Payoff">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-red-500" />
        </div>
      </Layout>
    );
  }

  const debts = overview?.debts || [];
  const summary = overview?.summary || {};
  const hasDebts = debts.length > 0;

  // Build timeline chart from avalanche data
  const timelineData = comparison?.avalanche?.timeline?.map((t) => ({
    month: t.month,
    Balance: t.total_balance,
  })) || [];

  // What-if chart data
  const whatIfData = whatIf?.scenarios?.map((s) => ({
    extra: `+$${s.extra_payment}`,
    Months: s.months,
    Interest: s.total_interest,
  })) || [];

  return (
    <Layout title="Debt Payoff">
      <div className="space-y-6" data-testid="debt-payoff-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Debt Payoff Planner</h1>
            <p className="text-sm text-zinc-500 mt-1">Snowball vs Avalanche strategies to crush your debt</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Label className="text-zinc-500 text-xs">Extra/mo:</Label>
              <Input
                type="number" min="0" step="50"
                value={extraPayment}
                onChange={(e) => setExtraPayment(e.target.value)}
                className="bg-black/50 border-white/10 rounded-sm w-24 text-sm"
                data-testid="extra-payment-input"
              />
              <Button
                onClick={() => runComparison(parseFloat(extraPayment) || 0)}
                disabled={comparing || !hasDebts}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                data-testid="recalculate-btn"
              >
                {comparing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Compare"}
              </Button>
            </div>
            {hasDebts && (
              <Button onClick={runAiPlan} disabled={aiLoading} variant="outline" size="sm" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-sm" data-testid="ai-plan-btn">
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5 mr-1.5" />}
                AI Plan
              </Button>
            )}
            <Button onClick={loadData} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="refresh-debt-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {!hasDebts ? (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="py-16 text-center">
              <Trophy className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-emerald-400 text-sm font-medium">Congratulations! No active debts found.</p>
              <p className="text-zinc-600 text-xs mt-1">Add loans, credit cards, or mortgages to use the payoff planner</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-total-debt">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Debt</p>
                  <p className="text-2xl font-bold font-mono text-red-400 mt-1">{fmt(summary.total_balance)}</p>
                  <p className="text-xs text-zinc-600 mt-1">{summary.total_debts} accounts</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-min-payment">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Min Monthly</p>
                  <p className="text-2xl font-bold font-mono text-amber-400 mt-1">{fmt(summary.total_minimum_payment)}</p>
                  <p className="text-xs text-zinc-600 mt-1">all minimums</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-avg-rate">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Avg Rate</p>
                  <p className="text-2xl font-bold font-mono text-white mt-1">{summary.weighted_avg_rate}%</p>
                  <p className="text-xs text-zinc-600 mt-1">weighted average</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-payoff-months">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Debt-Free In</p>
                  <p className="text-2xl font-bold font-mono text-emerald-400 mt-1">{comparison?.avalanche?.years || "?"} yrs</p>
                  <p className="text-xs text-zinc-600 mt-1">{comparison?.avalanche?.months || "?"} months (avalanche)</p>
                </CardContent>
              </Card>
            </div>

            {/* Debt List */}
            <div className="space-y-2">
              <h2 className="text-sm text-zinc-500 uppercase tracking-wider">Your Debts</h2>
              {debts.map((d) => {
                const Icon = typeIcons[d.type] || DollarSign;
                return (
                  <Card key={d.id} className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid={`debt-${d.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0 bg-red-500/10">
                          <Icon className="w-5 h-5 text-red-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">{d.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-zinc-400 rounded-sm capitalize">{d.type.replace("_", " ")}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-zinc-500 mt-0.5">
                            <span>{d.interest_rate}% APR</span>
                            <span>Min: {fmt(d.minimum_payment)}/mo</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-mono text-red-400 font-bold">{fmt(d.balance)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Strategy Comparison */}
            {comparison && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Avalanche */}
                  <Card className={`bg-[#0A0A0A] rounded-none ${comparison.comparison.recommended === "avalanche" ? "border-emerald-500/30" : "border-white/10"}`} data-testid="avalanche-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Flame className="w-4 h-4 text-orange-400" /> Avalanche
                        {comparison.comparison.recommended === "avalanche" && (
                          <span className="ml-auto text-[10px] px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-sm">Recommended</span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-[10px] text-zinc-600">Pay highest interest rate first</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white/[0.03] p-2 rounded-sm">
                          <p className="text-[10px] text-zinc-600">Payoff Time</p>
                          <p className="text-sm font-mono text-white">{comparison.avalanche.months} mo ({comparison.avalanche.years} yr)</p>
                        </div>
                        <div className="bg-white/[0.03] p-2 rounded-sm">
                          <p className="text-[10px] text-zinc-600">Total Interest</p>
                          <p className="text-sm font-mono text-red-400">{fmt(comparison.avalanche.total_interest)}</p>
                        </div>
                        <div className="bg-white/[0.03] p-2 rounded-sm">
                          <p className="text-[10px] text-zinc-600">Total Paid</p>
                          <p className="text-sm font-mono text-white">{fmt(comparison.avalanche.total_paid)}</p>
                        </div>
                        <div className="bg-emerald-500/5 p-2 rounded-sm">
                          <p className="text-[10px] text-emerald-400">Interest Saved</p>
                          <p className="text-sm font-mono text-emerald-400">{fmt(comparison.comparison.avalanche_interest_saved)}</p>
                        </div>
                      </div>
                      <div className="pt-1">
                        <p className="text-[10px] text-zinc-600 mb-1">Payoff Order:</p>
                        {comparison.avalanche.order.map((name, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs text-zinc-400">
                            <span className="text-[10px] text-zinc-600 w-4">{i + 1}.</span> {name}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Snowball */}
                  <Card className={`bg-[#0A0A0A] rounded-none ${comparison.comparison.recommended === "snowball" ? "border-emerald-500/30" : "border-white/10"}`} data-testid="snowball-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Snowflake className="w-4 h-4 text-blue-400" /> Snowball
                        {comparison.comparison.recommended === "snowball" && (
                          <span className="ml-auto text-[10px] px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-sm">Recommended</span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-[10px] text-zinc-600">Pay smallest balance first</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white/[0.03] p-2 rounded-sm">
                          <p className="text-[10px] text-zinc-600">Payoff Time</p>
                          <p className="text-sm font-mono text-white">{comparison.snowball.months} mo ({comparison.snowball.years} yr)</p>
                        </div>
                        <div className="bg-white/[0.03] p-2 rounded-sm">
                          <p className="text-[10px] text-zinc-600">Total Interest</p>
                          <p className="text-sm font-mono text-red-400">{fmt(comparison.snowball.total_interest)}</p>
                        </div>
                        <div className="bg-white/[0.03] p-2 rounded-sm">
                          <p className="text-[10px] text-zinc-600">Total Paid</p>
                          <p className="text-sm font-mono text-white">{fmt(comparison.snowball.total_paid)}</p>
                        </div>
                        <div className="bg-emerald-500/5 p-2 rounded-sm">
                          <p className="text-[10px] text-emerald-400">Interest Saved</p>
                          <p className="text-sm font-mono text-emerald-400">{fmt(comparison.comparison.snowball_interest_saved)}</p>
                        </div>
                      </div>
                      <div className="pt-1">
                        <p className="text-[10px] text-zinc-600 mb-1">Payoff Order:</p>
                        {comparison.snowball.order.map((name, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs text-zinc-400">
                            <span className="text-[10px] text-zinc-600 w-4">{i + 1}.</span> {name}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Minimum Only Comparison */}
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-zinc-500">Minimum Payments Only</p>
                        <p className="text-sm text-zinc-400">{comparison.minimum_only.months} months / {fmt(comparison.minimum_only.total_interest)} interest</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-emerald-400">With +{fmt(parseFloat(extraPayment) || 0)}/mo extra</p>
                        <p className="text-sm text-emerald-400 font-mono">
                          Save {comparison.comparison.avalanche_months_saved} months & {fmt(comparison.comparison.avalanche_interest_saved)} interest
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payoff Timeline Chart */}
                {timelineData.length > 1 && (
                  <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-emerald-400" /> Debt Payoff Timeline (Avalanche)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[250px]" data-testid="payoff-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={timelineData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                            <defs>
                              <linearGradient id="debtGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} label={{ value: "Months", position: "bottom", fill: "#52525b", fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "K" : v}`} width={50} />
                            <Tooltip content={<ChartTip />} />
                            <Area type="monotone" dataKey="Balance" stroke="#ef4444" fill="url(#debtGrad)" strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Per-Debt Details */}
                {comparison.avalanche.debt_details?.length > 0 && (
                  <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Target className="w-4 h-4 text-amber-400" /> Per-Debt Payoff Details (Avalanche)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto" data-testid="debt-details-table">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left text-zinc-500 py-2 px-3">Debt</th>
                              <th className="text-right text-zinc-500 py-2 px-3">Balance</th>
                              <th className="text-right text-zinc-500 py-2 px-3">Interest Paid</th>
                              <th className="text-right text-zinc-500 py-2 px-3">Total Paid</th>
                              <th className="text-right text-zinc-500 py-2 px-3">Paid Off</th>
                            </tr>
                          </thead>
                          <tbody>
                            {comparison.avalanche.debt_details.map((d) => (
                              <tr key={d.name} className="border-b border-white/5 hover:bg-white/[0.02]">
                                <td className="py-2.5 px-3 text-zinc-300">{d.name}</td>
                                <td className="py-2.5 px-3 text-right text-red-400 font-mono">{fmt(d.original_balance)}</td>
                                <td className="py-2.5 px-3 text-right text-amber-400 font-mono">{fmt(d.total_interest)}</td>
                                <td className="py-2.5 px-3 text-right text-white font-mono">{fmt(d.total_paid)}</td>
                                <td className="py-2.5 px-3 text-right text-emerald-400 font-mono">Month {d.payoff_month}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* What-If Simulator */}
            {whatIfData.length > 1 && (
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" /> What-If: Extra Payment Impact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[220px]" data-testid="whatif-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={whatIfData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                        <XAxis dataKey="extra" tick={{ fontSize: 11, fill: "#71717a" }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#71717a" }} label={{ value: "Months", angle: -90, position: "insideLeft", fill: "#52525b", fontSize: 10 }} width={45} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} width={50} />
                        <Tooltip content={<ChartTip />} />
                        <Legend wrapperStyle={{ fontSize: 11, color: "#71717a" }} />
                        <Bar yAxisId="left" dataKey="Months" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                        <Bar yAxisId="right" dataKey="Interest" fill="#ef4444" opacity={0.5} radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Savings table */}
                  <div className="mt-3 space-y-1">
                    {whatIf?.scenarios?.map((s, i) => {
                      const saved = (whatIf.baseline_interest || 0) - s.total_interest;
                      const monthsSaved = (whatIf.baseline_months || 0) - s.months;
                      return (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5">
                          <span className="text-zinc-400">+{fmt(s.extra_payment)}/mo extra</span>
                          <span className="text-zinc-300">{s.months} months ({s.years} yrs)</span>
                          <span className="text-emerald-400 font-mono">{saved > 0 ? `Save ${fmt(saved)}` : "Baseline"}</span>
                          <span className="text-cyan-400 font-mono">{monthsSaved > 0 ? `${monthsSaved} mo faster` : "-"}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Plan */}
            {aiPlan && (
              <Card className="bg-[#0A0A0A] border-cyan-500/10 rounded-none" data-testid="ai-plan-section">
                <CardHeader>
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-cyan-400" /> AI Debt Payoff Plan
                    {aiPlan.projected_debt_free_date && (
                      <span className="ml-auto text-xs text-emerald-400 font-normal">
                        Debt-free by: {aiPlan.projected_debt_free_date}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {aiPlan.reason && (
                    <div className="bg-white/[0.03] p-3 rounded-sm">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Strategy: {aiPlan.recommended_strategy}</p>
                      <p className="text-sm text-zinc-300">{aiPlan.reason}</p>
                    </div>
                  )}

                  {aiPlan.recommended_extra > 0 && (
                    <div className="bg-emerald-500/5 border border-emerald-500/10 p-3 rounded-sm">
                      <p className="text-xs text-emerald-400 uppercase tracking-wider">Recommended Extra Payment</p>
                      <p className="text-xl font-bold font-mono text-emerald-300 mt-1">{fmt(aiPlan.recommended_extra)}/mo</p>
                    </div>
                  )}

                  {aiPlan.payment_allocation?.length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Payment Allocation</p>
                      <div className="space-y-2">
                        {aiPlan.payment_allocation.map((a, i) => (
                          <div key={i} className="bg-white/[0.02] border border-white/5 p-3 rounded-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-white font-medium">#{a.priority} {a.debt}</span>
                              <span className="text-sm text-cyan-400 font-mono">{fmt(a.monthly_payment)}/mo</span>
                            </div>
                            <p className="text-xs text-zinc-400">{a.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiPlan.milestones?.length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Milestones</p>
                      <div className="space-y-1.5">
                        {aiPlan.milestones.map((m, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                            <Trophy className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                            <span className="text-xs text-zinc-600 font-mono w-16">Month {m.month}</span>
                            {m.event}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiPlan.monthly_savings_after_payoff > 0 && (
                    <div className="bg-cyan-500/5 border border-cyan-500/10 p-3 rounded-sm">
                      <p className="text-xs text-cyan-400 uppercase tracking-wider">After Payoff - Monthly Savings</p>
                      <p className="text-xl font-bold font-mono text-cyan-300 mt-1">{fmt(aiPlan.monthly_savings_after_payoff)}/mo</p>
                    </div>
                  )}

                  {aiPlan.warnings?.length > 0 && (
                    <div className="space-y-1.5">
                      {aiPlan.warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-amber-300">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {w}
                        </div>
                      ))}
                    </div>
                  )}

                  {aiPlan.motivation_tip && (
                    <p className="text-sm text-emerald-300 italic border-l-2 border-emerald-500/30 pl-3">{aiPlan.motivation_tip}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
